import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  IdCard, 
  Car, 
  FileCheck2, 
  Upload, 
  Download, 
  Eye, 
  Loader2, 
  CheckCircle2,
  RotateCw,
  Trash2,
  X,
  ZoomIn,
  Scissors,
  Scan,
  FileText,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { getPreviewUrl, downloadDocument, revokePreviewUrl } from '../lib/documentPreview';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import DocumentImageEditor from './DocumentImageEditor';
import Tesseract from 'tesseract.js';

// Document type configurations
const DOC_CONFIGS = {
  identity_card: {
    icon: IdCard,
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-400',
    hoverBorder: 'hover:border-blue-500',
    label: 'Kimlik Kartı',
    description: 'Ön ve arka yüz',
    hasTwoSides: true,
    altTypes: ['identiteitskaart']
  },
  driver_license: {
    icon: Car,
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-400',
    hoverBorder: 'hover:border-emerald-500',
    label: 'Ehliyet',
    description: 'Ön ve arka yüz',
    hasTwoSides: true,
    altTypes: ['rijbewijs']
  },
  passport: {
    icon: FileCheck2,
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-400',
    hoverBorder: 'hover:border-purple-500',
    label: 'Pasaport',
    description: 'Fotoğraflı sayfa',
    hasTwoSides: false,
    altTypes: ['paspoort']
  },
  bestuurdersattest: {
    icon: FileCheck2,
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-400',
    hoverBorder: 'hover:border-purple-500',
    label: 'BESTUURDERSATTEST',
    description: 'Sürücü Belgesi (AB)',
    hasTwoSides: false,
    altTypes: ['driver_attest', 'driver_attestation']
  }
};

// Individual document card with two-sided support
function DocumentCard({ 
  docType, 
  documents, 
  userId, 
  onUpload, 
  onDelete,
  onRefresh,
  t,
  onOcrComplete // Callback for OCR results
}) {
  const config = DOC_CONFIGS[docType];
  const Icon = config.icon;
  
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const [isDraggingFront, setIsDraggingFront] = useState(false);
  const [isDraggingBack, setIsDraggingBack] = useState(false);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [justUploadedSide, setJustUploadedSide] = useState(null); // 'front' | 'back' | null
  const [showBack, setShowBack] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [frontImageUrl, setFrontImageUrl] = useState(null);
  const [backImageUrl, setBackImageUrl] = useState(null);
  const [frontIsPdf, setFrontIsPdf] = useState(false);
  const [backIsPdf, setBackIsPdf] = useState(false);
  
  // Document Image Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSide, setEditorSide] = useState('front');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, doc: null });
  
  // Find documents for this type
  const allTypeKeys = [docType, ...config.altTypes];
  const frontDoc = documents.find(d => 
    allTypeKeys.includes(d.document_type) && 
    (d.side === 'front' || !d.side)
  );
  const backDoc = documents.find(d => 
    allTypeKeys.includes(d.document_type) && 
    d.side === 'back'
  );
  
  // Load images with auth (skip for PDFs)
  useEffect(() => {
    const loadImage = async (doc, setUrl, setIsPdf) => {
      if (!doc) {
        setUrl(null);
        setIsPdf(false);
        return;
      }
      const isPdf = doc.file_type?.toLowerCase() === 'pdf' || doc.file_type?.toLowerCase() === 'application/pdf';
      setIsPdf(isPdf);
      if (isPdf) {
        setUrl(null);
        return;
      }
      try {
        // Şub 27, 2026 — Direct CDN URL ile anında yükleme
        const { url: previewUrl } = await getPreviewUrl(api, doc.id, true);
        if (previewUrl) setUrl(previewUrl);
        else setUrl(null);
      } catch (error) {
        console.error('Error loading document image:', error);
        setUrl(null);
      }
    };
    
    loadImage(frontDoc, setFrontImageUrl, setFrontIsPdf);
    loadImage(backDoc, setBackImageUrl, setBackIsPdf);
    
    return () => {
      revokePreviewUrl(frontImageUrl);
      revokePreviewUrl(backImageUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontDoc?.id, backDoc?.id]);

  const handleDragOver = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'front') setIsDraggingFront(true);
    else setIsDraggingBack(true);
  };

  const handleDragLeave = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (side === 'front') setIsDraggingFront(false);
    else setIsDraggingBack(false);
  };

  const handleDrop = async (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'front') setIsDraggingFront(false);
    else setIsDraggingBack(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await handleUpload(files[0], side);
    }
  };

  const handleFileSelect = async (e, side) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUpload(file, side);
    }
    e.target.value = '';
  };

  const handleUpload = async (file, side) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('employeeProfile.unsupportedFileFormat') || 'Desteklenmeyen dosya formatı. JPG, PNG, PDF veya HEIC yükleyin.');
      return;
    }
    
    const setUploading = side === 'front' ? setUploadingFront : setUploadingBack;
    setUploading(true);
    
    try {
      // If it's an image and front side of ID or license, run OCR (browser-side)
      const isImage = file.type.startsWith('image/');
      const isPdfFile = file.type === 'application/pdf';
      
      if (isImage && side === 'front' && (docType === 'identity_card' || docType === 'driver_license')) {
        const ocrToastId = toast.loading('🔍 OCR: Belge taranıyor, lütfen bekleyin...', { duration: 60000 });
        
        try {
          const imageUrl = URL.createObjectURL(file);
          const result = await Tesseract.recognize(imageUrl, 'eng+tur+nld+fra+deu', {
            logger: m => {
              if (m.status === 'recognizing text' && m.progress) {
                toast.loading(`🔍 OCR: %${Math.round(m.progress * 100)} tamamlandı...`, { id: ocrToastId });
              }
            }
          });
          URL.revokeObjectURL(imageUrl);
          
          // Extract dates from OCR text
          const text = result.data.text;
          console.log('OCR Raw Text:', text);
          
          // Extended date patterns for Belgian and European ID cards
          const datePatterns = [
            /(\d{2})[.\/-](\d{2})[.\/-](\d{4})/g,  // DD.MM.YYYY
            /(\d{4})[.\/-](\d{2})[.\/-](\d{2})/g,  // YYYY.MM.DD
            /(\d{2})\s+(\d{2})\s+(\d{4})/g,       // DD MM YYYY (space separated - Belgian ID!)
            /(\d{1})[.\/-](\d{2})[.\/-](\d{4})/g,  // D.MM.YYYY (single digit day)
            /(\d{2})[.\/-](\d{1})[.\/-](\d{4})/g   // DD.M.YYYY (single digit month)
          ];
          
          // Month map for text months
          const monthMap = {
            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
            'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12',
            'MRT': '03', 'MEI': '05', 'OKT': '10', // Dutch
            'JANV': '01', 'FÉVR': '02', 'MARS': '03', 'AVR': '04', 'MAI': '05', 'JUIN': '06',
            'JUIL': '07', 'AOÛT': '08', 'SEPT': '09', 'OCTO': '10', 'NOVE': '11', 'DÉCE': '12' // French
          };
          
          // Check for Belgian ID/License keywords - 4b is expiry date for driver's license
          const expiryKeywords = ['GELDIG TOT', 'VALABLE', 'GÜLTIG BIS', 'VALID UNTIL', 'EXPIRY', 'VERVALT', '4B', '4B.'];
          let expiryContext = null;
          for (const keyword of expiryKeywords) {
            const idx = text.toUpperCase().indexOf(keyword);
            if (idx !== -1) {
              expiryContext = text.substring(idx, idx + 50);
              console.log(`Found expiry keyword "${keyword}":`, expiryContext);
              break;
            }
          }
          
          const foundDates = [];
          
          // Pattern 0: DD.MM.YYYY
          let match0;
          datePatterns[0].lastIndex = 0; // Reset regex state for consistent results
          while ((match0 = datePatterns[0].exec(text)) !== null) {
            const day = match0[1], month = match0[2], year = match0[3];
            const yearNum = parseInt(year), monthNum = parseInt(month), dayNum = parseInt(day);
            if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
              foundDates.push({ 
                date: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 
                yearNum,
                isNearExpiry: expiryContext && expiryContext.includes(match0[0])
              });
            }
          }
          
          // Pattern 1: YYYY.MM.DD
          let match1;
          datePatterns[1].lastIndex = 0; // Reset regex state
          while ((match1 = datePatterns[1].exec(text)) !== null) {
            const year = match1[1], month = match1[2], day = match1[3];
            const yearNum = parseInt(year), monthNum = parseInt(month), dayNum = parseInt(day);
            if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
              foundDates.push({ 
                date: `${year}-${month}-${day}`, 
                yearNum,
                isNearExpiry: expiryContext && expiryContext.includes(match1[0])
              });
            }
          }
          
          // Pattern 2: DD MM YYYY (space separated - Belgian ID!)
          let matchSpace;
          datePatterns[2].lastIndex = 0; // Reset regex state
          while ((matchSpace = datePatterns[2].exec(text)) !== null) {
            const day = matchSpace[1], month = matchSpace[2], year = matchSpace[3];
            const yearNum = parseInt(year), monthNum = parseInt(month), dayNum = parseInt(day);
            if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
              foundDates.push({ 
                date: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 
                yearNum,
                isNearExpiry: expiryContext && expiryContext.includes(matchSpace[0])
              });
              console.log('Found space-separated date:', `${day} ${month} ${year}`);
            }
          }
          
          // Pattern 3: D.MM.YYYY (single digit day)
          let match3;
          datePatterns[3].lastIndex = 0; // Reset regex state
          while ((match3 = datePatterns[3].exec(text)) !== null) {
            const day = match3[1].padStart(2, '0'), month = match3[2], year = match3[3];
            const yearNum = parseInt(year), monthNum = parseInt(month);
            if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12) {
              foundDates.push({ 
                date: `${year}-${month}-${day}`, 
                yearNum,
                isNearExpiry: expiryContext && expiryContext.includes(match3[0])
              });
            }
          }
          
          // Text month pattern: DD MMM YYYY
          const textMonthPattern = /(\d{1,2})\s*(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|MRT|MEI|OKT|JANV?|FÉVR?|MARS|AVR|MAI|JUIN|JUIL|AOÛT|SEPT|OCTO?|NOVE?|DÉCE?)[A-ZÉÛ]*\.?\s*(\d{4})/gi;
          let matchText;
          while ((matchText = textMonthPattern.exec(text)) !== null) {
            const day = matchText[1].padStart(2, '0');
            const monthStr = matchText[2].toUpperCase().replace(/[.]/g, '').substring(0, 4);
            const monthNum = monthMap[monthStr] || monthMap[monthStr.substring(0, 3)];
            const year = matchText[3];
            if (monthNum) {
              const yearNum = parseInt(year);
              if (yearNum >= 1950 && yearNum <= 2100) {
                foundDates.push({ 
                  date: `${year}-${monthNum}-${day}`, 
                  yearNum,
                  isNearExpiry: expiryContext && expiryContext.includes(matchText[0])
                });
              }
            }
          }
          
          // MRZ pattern: YYMMDD
          const mrzPattern = /([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])/g;
          const mrzMatches = text.match(mrzPattern);
          if (mrzMatches) {
            for (const mrzDate of mrzMatches) {
              const yy = parseInt(mrzDate.substring(0, 2));
              const mm = mrzDate.substring(2, 4);
              const dd = mrzDate.substring(4, 6);
              const year = yy > 50 ? `19${mrzDate.substring(0, 2)}` : `20${mrzDate.substring(0, 2)}`;
              const yearNum = parseInt(year);
              if (yearNum >= 1950 && yearNum <= 2100) {
                foundDates.push({ date: `${year}-${mm}-${dd}`, yearNum, fromMRZ: true });
              }
            }
          }
          
          // Extract RIJKSREGISTERNR (Belgian national ID number)
          // Format: YY.MM.DD-XXX.YY or YY MM DD XXX YY or variations
          let rijksregisternr = null;
          const rrPatterns = [
            /(\d{2})[.\s](\d{2})[.\s](\d{2})[.\s-](\d{3})[.\s-](\d{2})/g,  // YY.MM.DD-XXX.YY
            /(\d{2})\.(\d{2})\.(\d{2})[-\s](\d{3})\.(\d{2})/g,              // YY.MM.DD-XXX.YY with dots
          ];
          for (const rrPattern of rrPatterns) {
            rrPattern.lastIndex = 0;
            const rrMatch = rrPattern.exec(text);
            if (rrMatch) {
              rijksregisternr = `${rrMatch[1]}.${rrMatch[2]}.${rrMatch[3]}-${rrMatch[4]}.${rrMatch[5]}`;
              console.log('Found RIJKSREGISTERNR:', rijksregisternr);
              break;
            }
          }
          // Also try to find near keyword
          if (!rijksregisternr) {
            const rrKeywords = ['RIJKSREGISTERNR', 'REGISTERNR', 'NAT.REG', 'Reg.Nr', 'CARTE', 'NR.'];
            for (const kw of rrKeywords) {
              const kwIdx = text.toUpperCase().indexOf(kw);
              if (kwIdx !== -1) {
                const nearby = text.substring(kwIdx, kwIdx + 60);
                const nearbyMatch = nearby.match(/(\d{2})[.\s](\d{2})[.\s](\d{2})[.\s-](\d{3})[.\s-](\d{2})/);
                if (nearbyMatch) {
                  rijksregisternr = `${nearbyMatch[1]}.${nearbyMatch[2]}.${nearbyMatch[3]}-${nearbyMatch[4]}.${nearbyMatch[5]}`;
                  console.log('Found RIJKSREGISTERNR near keyword:', rijksregisternr);
                  break;
                }
              }
            }
          }

          const uniqueDates = [...new Set(foundDates.map(d => d.date))].map(date => {
            const found = foundDates.find(d => d.date === date);
            return { date, yearNum: parseInt(date.split('-')[0]), isNearExpiry: found?.isNearExpiry || false };
          });
          
          console.log('All found dates:', uniqueDates);
          
          const currentYear = new Date().getFullYear();
          const pastDates = uniqueDates.filter(d => d.yearNum < currentYear);
          const futureDates = uniqueDates.filter(d => d.yearNum >= currentYear);
          
          // First check if any date is near expiry keywords (like "4b")
          let expiryDate = uniqueDates.find(d => d.isNearExpiry)?.date || null;
          
          // For driver's license: take the FURTHEST future date (4b > 4a)
          // 4a = issue date (2025), 4b = expiry date (2035)
          if (!expiryDate && futureDates.length > 0) {
            futureDates.sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort ascending
            // For driver's license with multiple future dates, take the FURTHEST one (4b)
            if (docType === 'driver_license' && futureDates.length > 1) {
              expiryDate = futureDates[futureDates.length - 1].date; // Furthest = last
              console.log('Driver license: taking furthest future date (4b):', expiryDate);
            } else {
              expiryDate = futureDates[futureDates.length - 1].date;
            }
          }
          
          // Birth date: oldest date in the past
          let birthDate = pastDates.length > 0 ? pastDates.sort((a, b) => new Date(a.date) - new Date(b.date))[0].date : null;
          
          // If still no expiry date but we have multiple dates, take the most recent non-birth date
          if (!expiryDate && uniqueDates.length > 1) {
            const sorted = [...uniqueDates].sort((a, b) => new Date(b.date) - new Date(a.date));
            expiryDate = sorted[0].date !== birthDate ? sorted[0].date : (sorted[1]?.date || null);
          }
          
          // Dismiss loading toast
          toast.dismiss(ocrToastId);
          
          if (expiryDate || birthDate) {
            toast.success(
              `✅ OCR Tamamlandı!\n` +
              (expiryDate ? `📅 Son Kullanma: ${new Date(expiryDate).toLocaleDateString('tr-TR')}\n` : '') +
              (birthDate ? `🎂 Doğum Tarihi: ${new Date(birthDate).toLocaleDateString('tr-TR')}\n` : '') +
              (rijksregisternr ? `🆔 Rijksregisternr: ${rijksregisternr}` : ''),
              { duration: 5000 }
            );
            
            if (onOcrComplete) {
              onOcrComplete({
                docType,
                text: text.substring(0, 500),
                birthDate,
                expiryDate,
                rijksregisternr,
                allDates: uniqueDates.map(d => d.date)
              });
            }
          } else if (uniqueDates.length > 0) {
            toast.warning(
              `⚠️ OCR: ${uniqueDates.length} tarih bulundu ama son kullanma tarihi belirlenemedi.\n` +
              `Bulunan: ${uniqueDates.map(d => new Date(d.date).toLocaleDateString('tr-TR')).join(', ')}`,
              { duration: 6000 }
            );
          } else {
            toast.error('❌ OCR: Belgede tarih bulunamadı. Lütfen daha net bir görüntü yükleyin veya manuel girin.', { duration: 5000 });
          }
        } catch (ocrError) {
          console.error('OCR Error:', ocrError);
          toast.dismiss(ocrToastId);
          toast.error('❌ OCR Hatası: ' + (ocrError.message || 'Bilinmeyen hata'), { duration: 4000 });
        }
      }
      
      const formData = new FormData();
      formData.append('files', file);
      
      const sideLabel = side === 'front' ? (t('employeeProfile.frontSide') || 'Ön Yüz') : (t('employeeProfile.backSide') || 'Arka Yüz');
      formData.append('file_names', `${config.label} - ${sideLabel}`);
      formData.append('category', 'PERSONAL');
      formData.append('assigned_to', userId);
      formData.append('document_type', docType);
      formData.append('side', side);

      const response = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const uploadedIds = (response.data.data || []).map(d => d.id).filter(Boolean);
        // Custom toast with a real thumbnail + "Geri Al" (undo) so the admin
        // gets a visual confirmation of which file landed in which slot AND
        // can undo a mistake within 5.5s (especially during PDF OCR which
        // delays the card refresh).
        const isImg = file.type.startsWith('image/');
        const thumbUrl = isImg ? URL.createObjectURL(file) : null;
        const toastId = `upload-thumb-${Date.now()}`;
        const duration = uploadedIds.length ? 5500 : 3500;
        toast.custom(
          (tid) => (
            <div className="flex items-center gap-3 w-[340px] p-3 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-emerald-200 dark:border-emerald-800">
              <div className="shrink-0 w-12 h-12 rounded overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                {thumbUrl ? (
                  <img src={thumbUrl} alt={file.name} className="w-full h-full object-cover" />
                ) : (
                  <FileText className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3.5 w-3.5 stroke-[3]" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Yüklendi</span>
                </div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate mt-0.5">
                  {config.label} <span className="text-slate-400 font-normal">({sideLabel})</span>
                </p>
                <p className="text-[11px] text-slate-500 truncate">{file.name}</p>
              </div>
              {uploadedIds.length > 0 && (
                <button
                  onClick={async () => {
                    toast.dismiss(tid);
                    try {
                      await Promise.all(uploadedIds.map(id => api.delete(`/documents/${id}`)));
                      toast.success('Yükleme geri alındı');
                      onRefresh?.();
                    } catch {
                      toast.error('Geri alma başarısız');
                    }
                  }}
                  className="shrink-0 text-xs font-semibold text-slate-600 hover:text-red-600 dark:text-slate-300 dark:hover:text-red-400 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:border-red-300 transition-colors"
                  data-testid={`undo-upload-${docType}-${side}`}
                >
                  Geri Al
                </button>
              )}
            </div>
          ),
          { id: toastId, duration },
        );
        if (thumbUrl) setTimeout(() => URL.revokeObjectURL(thumbUrl), duration + 500);

        // Show the inline "✓ Yüklendi" overlay on the slot for ~1.8s so the
        // admin visually confirms the file went into the correct slot. This
        // is especially helpful during PDF OCR which can delay the refresh.
        setJustUploadedSide(side);
        setTimeout(() => setJustUploadedSide(null), 1800);
        
        // If PDF was uploaded and it's front side of ID/license/bestuurdersattest, run backend OCR
        if (isPdfFile && side === 'front' && (docType === 'identity_card' || docType === 'driver_license' || docType === 'bestuurdersattest')) {
          const uploadedDocId = response.data.data?.[0]?.id;
          if (uploadedDocId) {
            const ocrToastId = toast.loading('PDF OCR: Metin çıkarılıyor...', { duration: 30000 });
            try {
              const ocrResponse = await api.post(`/documents/${uploadedDocId}/ocr`);
              toast.dismiss(ocrToastId);
              if (ocrResponse.data.success) {
                const ocrData = ocrResponse.data.data;
                // Always trigger the confirm modal so the admin sees what was
                // (or wasn't) detected and can manually enter the date if the
                // OCR couldn't determine it — the expiry date is too important
                // to silently skip.
                if (ocrData.expiry_date || ocrData.birth_date) {
                  toast.success(
                    `PDF OCR Tamamlandı!\n` +
                    (ocrData.expiry_date ? `Son Kullanma: ${new Date(ocrData.expiry_date).toLocaleDateString('tr-TR')}\n` : '') +
                    (ocrData.birth_date ? `Doğum Tarihi: ${new Date(ocrData.birth_date).toLocaleDateString('tr-TR')}` : ''),
                    { duration: 5000 }
                  );
                } else if (ocrData.found_dates?.length > 0) {
                  toast.warning(
                    `PDF OCR: ${ocrData.found_dates.length} tarih bulundu. Lütfen doğru tarihi seçin.`,
                    { duration: 6000 }
                  );
                } else {
                  toast.info(
                    `PDF OCR: ${ocrData.pages} sayfa tarandı, tarih bulunamadı. Lütfen tarihi manuel girin.`,
                    { duration: 5000 }
                  );
                }
                if (onOcrComplete) {
                  onOcrComplete({
                    docType,
                    text: ocrData.text,
                    birthDate: ocrData.birth_date,
                    expiryDate: ocrData.expiry_date,
                    allDates: ocrData.found_dates || []
                  });
                }
              }
            } catch (ocrErr) {
              toast.dismiss(ocrToastId);
              console.error('PDF OCR error:', ocrErr);
            }
          }
        }
        
        onRefresh();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('employeeProfile.documentUploadError') || 'Belge yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!doc) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t('employeeProfile.documentDeleted') || 'Belge silindi');
      setDeleteConfirm({ open: false, doc: null });
      onRefresh();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t('employeeProfile.documentDeleteError') || 'Belge silinemedi');
    }
  };

  const handleDeleteClick = (doc) => {
    setDeleteConfirm({ open: true, doc });
  };

  // Open Document Image Editor
  const openEditor = (side) => {
    setEditorSide(side);
    setEditorOpen(true);
  };

  // Handle file from Document Image Editor
  const handleEditorComplete = async (file) => {
    if (!file) return;
    await handleUpload(file, editorSide);
    setEditorOpen(false);
  };

  const handleDownload = async (doc) => {
    if (!doc) return;
    const ok = await downloadDocument(api, doc);
    if (!ok) toast.error(t('employeeProfile.downloadError') || 'İndirme hatası');
  };

  // Render upload zone
  const renderUploadZone = (side, isDragging, uploading, existingDoc, imageUrl) => {
    const isUploading = side === 'front' ? uploadingFront : uploadingBack;
    const inputRef = side === 'front' ? frontInputRef : backInputRef;
    const isPdf = side === 'front' ? frontIsPdf : backIsPdf;
    const sideLabel = side === 'front' ? (t('employeeProfile.frontSide') || 'Ön Yüz') : (t('employeeProfile.backSide') || 'Arka Yüz');
    
    return (
      <div 
        className={`
          relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer
          ${existingDoc && (imageUrl || isPdf)
            ? 'border-2 border-solid'
            : 'border-2 border-dashed'
          }
          ${isDragging 
            ? `${config.borderColor} bg-${config.color}-50 scale-[1.02]` 
            : existingDoc 
              ? 'border-green-300 bg-green-50/30' 
              : `border-slate-300 ${config.hoverBorder} hover:bg-slate-50`
          }
        `}
        style={{ aspectRatio: '1.6' }}
        onDragOver={(e) => handleDragOver(e, side)}
        onDragEnter={(e) => handleDragEnter(e, side)}
        onDragLeave={(e) => handleDragLeave(e, side)}
        onDrop={(e) => handleDrop(e, side)}
        onClick={() => !existingDoc && inputRef.current?.click()}
      >
        {/* Success animation overlay — shown for ~1.8s after a successful upload */}
        {justUploadedSide === side && (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-emerald-500/90 animate-in fade-in zoom-in duration-300 pointer-events-none"
            data-testid={`doc-success-${docType}-${side}`}
          >
            <div className="bg-white rounded-full p-2 shadow-lg animate-bounce">
              <Check className="h-7 w-7 text-emerald-600 stroke-[3]" />
            </div>
            <p className="text-white text-sm font-semibold mt-2 drop-shadow">Yüklendi</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
          onChange={(e) => handleFileSelect(e, side)}
          data-testid={`doc-upload-${docType}-${side}`}
        />
        
        {existingDoc && isPdf ? (
          // PDF placeholder
          <div className="relative w-full h-full group flex flex-col items-center justify-center bg-red-50 dark:bg-red-900/20">
            <FileText className="h-10 w-10 text-red-500 mb-1" />
            <p className="text-xs font-semibold text-red-600">PDF</p>
            <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[90%] px-2">{existingDoc.original_name || existingDoc.file_name}</p>
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); setPreviewDoc(existingDoc); }}
                data-testid={`doc-preview-${docType}-${side}`}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDownload(existingDoc); }}
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              >
                <Upload className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(existingDoc); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {/* Side label badge */}
            <Badge className={`absolute top-2 left-2 ${config.bgColor} ${config.textColor} text-xs`}>
              {sideLabel}
            </Badge>
          </div>
        ) : existingDoc && imageUrl ? (
          // Show uploaded image
          <div className="relative w-full h-full group">
            <img 
              src={imageUrl} 
              alt={`${config.label} ${sideLabel}`}
              className="w-full h-full object-cover"
            />
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); setPreviewDoc(existingDoc); }}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDownload(existingDoc); }}
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(existingDoc); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {/* Side label badge */}
            <Badge className={`absolute top-2 left-2 ${config.bgColor} ${config.textColor} text-xs`}>
              {sideLabel}
            </Badge>
          </div>
        ) : isUploading ? (
          // Uploading state
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Loader2 className={`h-8 w-8 ${config.textColor} animate-spin mb-2`} />
            <p className="text-sm text-slate-500">Yükleniyor...</p>
          </div>
        ) : isDragging ? (
          // Dragging state
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Upload className={`h-10 w-10 ${config.textColor} mb-2`} />
            <p className={`text-sm font-medium ${config.textColor}`}>Bırakın</p>
          </div>
        ) : (
          // Empty state
          <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
            <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center mb-2`}>
              <Upload className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <p className="text-xs font-medium text-slate-700 text-center">{sideLabel}</p>
            <p className="text-xs text-slate-400 text-center mt-1">Sürükle-bırak veya tıkla</p>
            {/* Edit & Upload button for advanced editing */}
            <Button
              size="sm"
              variant="ghost"
              className="mt-2 h-7 text-xs gap-1"
              onClick={(e) => {
                e.stopPropagation();
                openEditor(side);
              }}
            >
              <Scissors className="h-3 w-3" />
              {t('employeeProfile.cropAndEdit') || 'Kes ve Yükle'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const hasFront = !!frontDoc;
  const hasBack = !!backDoc;
  const isComplete = config.hasTwoSides ? (hasFront && hasBack) : hasFront;

  return (
    <>
      <div className={`bg-white dark:bg-slate-800 rounded-xl p-4 border-2 ${isComplete ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
              <Icon className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <div>
              <p className="font-semibold text-slate-800 dark:text-slate-100">{config.label}</p>
              <p className="text-xs text-slate-500">{config.description}</p>
            </div>
          </div>
          {isComplete && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Tamam
            </Badge>
          )}
        </div>

        {/* Two-sided upload zones */}
        {config.hasTwoSides ? (
          <div className="grid grid-cols-2 gap-3">
            {renderUploadZone('front', isDraggingFront, uploadingFront, frontDoc, frontImageUrl)}
            {renderUploadZone('back', isDraggingBack, uploadingBack, backDoc, backImageUrl)}
          </div>
        ) : (
          // Single upload zone for passport
          <div className="max-w-[200px] mx-auto">
            {renderUploadZone('front', isDraggingFront, uploadingFront, frontDoc, frontImageUrl)}
          </div>
        )}

        {/* Toggle for viewing sides (if both uploaded) */}
        {config.hasTwoSides && hasFront && hasBack && (
          <div className="flex justify-center mt-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-slate-500"
              onClick={() => {
                if (showBack) {
                  setPreviewDoc(frontDoc);
                } else {
                  setPreviewDoc(backDoc);
                }
                setShowBack(!showBack);
              }}
            >
              <RotateCw className="h-3 w-3 mr-1" />
              {showBack ? (t('employeeProfile.showFrontSide') || 'Ön yüzü göster') : (t('employeeProfile.showBackSide') || 'Arka yüzü göster')}
            </Button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        title={previewDoc ? `${config.label}` : ''}
      />

      {/* Document Image Editor Modal */}
      <DocumentImageEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onComplete={handleEditorComplete}
        documentType={docType}
        title={`${config.label} - ${editorSide === 'front' ? (t('employeeProfile.frontSide') || 'Ön Yüz') : (t('employeeProfile.backSide') || 'Arka Yüz')}`}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm({ open: false, doc: null })}>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-sm mx-4 w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('employeeProfile.deleteDocumentTitle') || 'Belgeyi Sil'}
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-300 mb-2">
              {t('employeeProfile.deleteDocumentConfirm') || 'Bu belgeyi silmek istediğinizden emin misiniz?'}
            </p>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
              {config.label} - {deleteConfirm.doc?.side === 'back' ? (t('employeeProfile.backSide') || 'Arka Yüz') : (t('employeeProfile.frontSide') || 'Ön Yüz')}
            </p>
            <p className="text-xs text-red-500 mb-5">
              Bu işlem geri alınamaz.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                data-testid="delete-doc-cancel"
                onClick={() => setDeleteConfirm({ open: false, doc: null })}
              >
                {t('common.cancel') || 'İptal'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                data-testid="delete-doc-confirm"
                onClick={() => handleDelete(deleteConfirm.doc)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {t('common.delete') || 'Sil'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Main component
export function ImportantDocumentsCard({ 
  documents = [], 
  userId, 
  onRefresh,
  onOcrComplete,
  t = (key) => key 
}) {
  return (
    <Card className="border-2 border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-amber-800 dark:text-amber-200">
          <IdCard className="h-5 w-5" />
          {t('employeeProfile.importantDocuments') || 'Önemli Belgeler'}
        </CardTitle>
        <CardDescription className="text-amber-700 dark:text-amber-300">
          {t('employeeProfile.importantDocumentsDesc') || 'Kimlik, Sürücü Belgesi ve Ehliyet belgeleriniz'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DocumentCard 
            docType="identity_card"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            onOcrComplete={onOcrComplete}
            t={t}
          />
          <DocumentCard 
            docType="bestuurdersattest"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            onOcrComplete={onOcrComplete}
            t={t}
          />
          <DocumentCard 
            docType="driver_license"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            onOcrComplete={onOcrComplete}
            t={t}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default ImportantDocumentsCard;
